import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { MultiSelect } from '../multi-select';
import { Filter, SetFilterValue } from './filter-types';
import { FilterElement } from './FilterElement';

import { useTranslation } from 'react-i18next';

export const ComboFilterElement = ({
  id,
  filters,
  mode,
  filterValues,
  setFilterValue,
  removeFilter,
  wrapperClassName,
}: {
  id: string;
  filters: Filter[];
  mode: string;
  filterValues: Record<string, unknown>;
  setFilterValue: SetFilterValue;
  removeFilter: (filter?: Filter | undefined) => void;
  wrapperClassName?: string;
}) => {
  const { t } = useTranslation('designSystem');
  const selectedFilterId = (filterValues[id] as string) || filters?.[0]?.id;
  const selectedFilter = filters.find((f) => f.id === selectedFilterId);

  // Query mode derives the default from the first filter without writing it
  // to the URL. State mode has no URL, so the default is stored on mount.
  useEffect(() => {
    if (mode === 'state') {
      setFilterValue(id, selectedFilterId, selectedFilter);
    }
    // Mount-only: the default is stored once, from the first render's values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // remove all other filters when one is selected
  useEffect(() => {
    for (const filter of filters) {
      if (filter.id !== selectedFilterId) {
        setFilterValue(filter.id, undefined, filter, { replace: true });
      }
    }
  }, [filterValues[id]]);

  return (
    <div className="combined-filter flex flex-row">
      <MultiSelect
        value={selectedFilterId}
        setValue={(filterId) => {
          setFilterValue(id, filterId);
        }}
        options={filters.map((f) => ({
          value: f.id,
          label: f.label,
        }))}
        mode="single"
        label={t('selectFilter')}
        key={id}
        showOperator={false}
        displayButtonClassName={`rounded-tr-none rounded-br-none ${wrapperClassName}`}
        icon={ChevronDownIcon}
        iconPosition="right"
        itemDisplay={({ item }) => (
          <div className="inline-flex cursor-pointer items-center truncate p-2 pr-4 text-sm">
            {item.label || item.value}
          </div>
        )}
      />
      {selectedFilterId && (
        <div className="min-w-0 flex-1">
          <FilterElement
            key={selectedFilterId}
            filter={filters.find((f) => f.id === selectedFilterId) as Filter}
            filters={filters}
            mode={mode}
            filterValues={filterValues}
            setFilterValue={setFilterValue}
            removeFilter={removeFilter}
          />
        </div>
      )}
    </div>
  );
};
